import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ROLE_LABELS, useAuth, type Role } from "./auth";
import { t } from "@/lib/i18n";

/* Команды с сервера (/api/teams) — общие для окна «Настройки команды» и вкладки «Команды» в настройках.
   Создавать, менять состав и удалять могут владелец, администраторы и менеджеры; остальные только смотрят. */

export type TeamPerson = { id: string; name: string; email: string; role: string; online: boolean };

export type HubTeam = { id: string; name: string; description: string; members: string[]; createdAt: string };

const EVENT = "hub:teams";

export function useTeams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<HubTeam[]>([]);
  const [people, setPeople] = useState<TeamPerson[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [list, u] = await Promise.all([
        api<HubTeam[]>("/teams"),
        api<{ id: string; name: string; email: string; role: Role; status: string; online: boolean }[]>("/users"),
      ]);
      setTeams(list);
      setPeople(u.filter((x) => x.status !== "disabled").map((x) => ({ id: x.id, name: x.name, email: x.email, role: t(ROLE_LABELS[x.role] ?? x.role), online: x.online })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
    const on = () => void load().catch(() => undefined);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, [load]);

  const changed = () => window.dispatchEvent(new Event(EVENT));

  const create = useCallback(async (body: { name: string; description: string; members: string[] }) => {
    const team = await api<HubTeam>("/teams", { method: "POST", json: body });
    changed();
    return team;
  }, []);

  const update = useCallback(async (id: string, body: Partial<{ name: string; description: string; members: string[] }>) => {
    const team = await api<HubTeam>(`/teams/${id}`, { method: "PATCH", json: body });
    changed();
    return team;
  }, []);

  const remove = useCallback(async (id: string) => {
    await api(`/teams/${id}`, { method: "DELETE" });
    changed();
  }, []);

  const nameOf = useCallback((id: string) => people.find((p) => p.id === id)?.name ?? "—", [people]);

  return { teams, people, loading, create, update, remove, nameOf, canManage: Boolean(user && ["owner", "admin", "manager"].includes(user.role)) };
}
